/** Takeoff tool icons as path data, so the web (DOM svg) and React Native (react-native-svg)
 *  draw the same artwork from one place.
 *
 *  The measurement glyphs reuse the constants the exported sheet legend already reads
 *  (measurementGlyphPaths), and the Material glyphs come from iconPaths, so none of
 *  the shapes are restated here. A layer painted `ink` takes the caller's colour; any
 *  other paint is fixed in every theme. */
import {
  d_arrow_range, d_chat_bubble, d_document_takeoff, d_description, d_cloud, d_frame_inspect, d_highlight_alt, d_north_east,
  d_open_in_full, d_search, d_settings, d_table_chart, d_text_fields, d_view_column, d_view_in_ar,
} from '../../shared/ui/iconPaths';
import {
  AREA_GLYPH_FILL_OPACITY, AREA_GLYPH_PATH, AREA_GLYPH_STROKE, COUNT_GLYPH_DOTS, GLYPH_GRID,
  LINEAR_GLYPH_PATH, LINEAR_GLYPH_STROKE, VOLUME_GLYPH_FILL_OPACITY, VOLUME_GLYPH_PATH, VOLUME_GLYPH_STROKE,
} from '../../features/takeoff/tools/measurementGlyphPaths';

/** Painted with whatever colour the renderer is given (the web's currentColor). */
export const INK = 'ink';

export interface GlyphLayer {
  d: string;
  fill?: string;
  fillOpacity?: number;
  stroke?: string;
  strokeWidth?: number;
  strokeLinecap?: 'butt' | 'round' | 'square';
  strokeLinejoin?: 'miter' | 'round' | 'bevel';
}

export interface ToolGlyph {
  viewBox: string;
  layers: GlyphLayer[];
}

/** Keeps each entry typed as ToolGlyph so optional layer fields stay readable. */
const glyph = (value: ToolGlyph): ToolGlyph => value;

const MATERIAL_VIEW_BOX = '0 -960 960 960';
const GRID_VIEW_BOX = `0 0 ${GLYPH_GRID} ${GLYPH_GRID}`;

export function materialGlyph(d: string): ToolGlyph {
  return { viewBox: MATERIAL_VIEW_BOX, layers: [{ d, fill: INK }] };
}

function strokedGlyph(d: string, strokeWidth = LINEAR_GLYPH_STROKE): ToolGlyph {
  return {
    viewBox: GRID_VIEW_BOX,
    layers: [{ d, fill: 'none', stroke: INK, strokeWidth, strokeLinecap: 'round', strokeLinejoin: 'round' }],
  };
}

/** The nav tools' icons are the pointers they set, drawn the way the OS draws a cursor: a
 *  white body under a dark halo, fixed colours in every theme. The halo is a fill plus a fat
 *  stroke (an expanded silhouette) and the body paints over it, so a multi-shape glyph
 *  (the hand) stays a clean union rather than drawing halo lines through its neighbours. */
const POINTER_HALO = '#1b1b1d';
const POINTER_BODY = '#ffffff';

function pointerGlyph(shapes: string[]): ToolGlyph {
  return {
    viewBox: GRID_VIEW_BOX,
    layers: [
      ...shapes.map((d): GlyphLayer => ({ d, fill: POINTER_HALO, stroke: POINTER_HALO, strokeWidth: 2.4, strokeLinejoin: 'round' })),
      ...shapes.map((d): GlyphLayer => ({ d, fill: POINTER_BODY })),
    ],
  };
}

const dot = (cx: number, cy: number, r: number) =>
  `M${cx - r} ${cy} a${r} ${r} 0 1 0 ${2 * r} 0 a${r} ${r} 0 1 0 ${-2 * r} 0 Z`;

export const TOOL_GLYPHS = {
  select: pointerGlyph(['M7.6 2.5 L7.6 18.6 L11.4 14.9 L13.6 20.2 L16.2 19.1 L14 13.9 L19.2 13.4 Z']),
  // Rounded rectangles flattened to paths (the fingers, the thumb rotated -34 degrees, the palm).
  pan: pointerGlyph([
    'M8.5 4.4 A1.3 1.3 0 0 1 9.8 5.7 L9.8 12.1 A1.3 1.3 0 0 1 8.5 13.4 A1.3 1.3 0 0 1 7.2 12.1 L7.2 5.7 A1.3 1.3 0 0 1 8.5 4.4 Z',
    'M11.5 3.2 A1.3 1.3 0 0 1 12.8 4.5 L12.8 11.9 A1.3 1.3 0 0 1 11.5 13.2 A1.3 1.3 0 0 1 10.2 11.9 L10.2 4.5 A1.3 1.3 0 0 1 11.5 3.2 Z',
    'M14.5 3.9 A1.3 1.3 0 0 1 15.8 5.2 L15.8 12.1 A1.3 1.3 0 0 1 14.5 13.4 A1.3 1.3 0 0 1 13.2 12.1 L13.2 5.2 A1.3 1.3 0 0 1 14.5 3.9 Z',
    'M17.4 5.4 A1.3 1.3 0 0 1 18.7 6.7 L18.7 12.1 A1.3 1.3 0 0 1 17.4 13.4 A1.3 1.3 0 0 1 16.1 12.1 L16.1 6.7 A1.3 1.3 0 0 1 17.4 5.4 Z',
    'M2.76 10.35 A1.35 1.35 0 0 1 4.63 10.71 L6.7 13.78 A1.35 1.35 0 0 1 6.34 15.65 A1.35 1.35 0 0 1 4.47 15.29 L2.4 12.22 A1.35 1.35 0 0 1 2.76 10.35 Z',
    'M9.8 10.4 L14.3 10.4 A4.4 4.4 0 0 1 18.7 14.8 L18.7 15 A4.4 4.4 0 0 1 14.3 19.4 L9.8 19.4 A4.4 4.4 0 0 1 5.4 15 L5.4 14.8 A4.4 4.4 0 0 1 9.8 10.4 Z',
  ]),
  orbit: pointerGlyph(['M12 1.8 L15.1 5.4 L13.1 5.4 L13.1 9.9 L17.6 9.9 L17.6 7.9 L21.2 11 L17.6 14.1 L17.6 12.1 L13.1 12.1 L13.1 16.6 L15.1 16.6 L12 20.2 L8.9 16.6 L10.9 16.6 L10.9 12.1 L6.4 12.1 L6.4 14.1 L2.8 11 L6.4 7.9 L6.4 9.9 L10.9 9.9 L10.9 5.4 L8.9 5.4 Z']),
  zoomWindow: materialGlyph(d_frame_inspect),
  area: glyph({
    viewBox: GRID_VIEW_BOX,
    layers: [{ d: AREA_GLYPH_PATH, fill: INK, fillOpacity: AREA_GLYPH_FILL_OPACITY, stroke: INK, strokeWidth: AREA_GLYPH_STROKE, strokeLinejoin: 'round' }],
  }),
  length: strokedGlyph(LINEAR_GLYPH_PATH),
  // Not a rail tool (Volume is an Area with a depth) but a type can still carry it.
  volume: glyph({
    viewBox: GRID_VIEW_BOX,
    layers: [{ d: VOLUME_GLYPH_PATH, fill: INK, fillOpacity: VOLUME_GLYPH_FILL_OPACITY, stroke: INK, strokeWidth: VOLUME_GLYPH_STROKE, strokeLinejoin: 'round' }],
  }),
  count: glyph({
    viewBox: GRID_VIEW_BOX,
    layers: COUNT_GLYPH_DOTS.map((d): GlyphLayer => ({ d: dot(d.cx, d.cy, d.r), fill: INK })),
  }),
  quickMeasure: materialGlyph(d_arrow_range),
  // Two rays from a vertex with a short arc between them.
  angle: strokedGlyph('M 4 20 L 20 20 M 4 20 L 17 6 M 11 20 A 7 7 0 0 0 9.2 15.4'),
  cutout: materialGlyph(d_highlight_alt),
  cloud: materialGlyph(d_cloud),
  callout: materialGlyph(d_chat_bubble),
  textNote: materialGlyph(d_text_fields),
  arrow: materialGlyph(d_north_east),
  // A run with a perpendicular tick at each end, distinct from Linear's bare polyline.
  dimension: strokedGlyph('M 4 8 L 4 16 M 20 8 L 20 16 M 4 12 L 20 12'),
  // Gallery glyphs: a drawing with takeoff, a drawing placeholder.
  documentTakeoff: glyph({ viewBox: GRID_VIEW_BOX, layers: [{ d: d_document_takeoff, fill: INK }] }),
  drawing: materialGlyph(d_description),
  model: materialGlyph(d_view_in_ar),
  view3d: materialGlyph(d_view_in_ar),
  summary: materialGlyph(d_table_chart),
  fitToView: materialGlyph(d_open_in_full),
  textSearch: materialGlyph(d_search),
  compareRevisions: materialGlyph(d_view_column),
  viewerSettings: materialGlyph(d_settings),
} as const satisfies Record<string, ToolGlyph>;

export type ToolGlyphName = keyof typeof TOOL_GLYPHS;
